import { z } from 'zod'

/**
 * 상품·가격 API 계약.
 *
 * 세 층위:
 *  1. 포비즈 상품(product) — 운영자가 만드는 노출용 상품. base_price.
 *  2. 업체 공급 상품(vendor_product) — 업체가 공급하는 상품. base_supply_price. 발주(M3)에 쓰인다.
 *  3. 기업별 상품(company_product) — 기업에 어떤 상품을 얼마에 파는지. 주문(BIZ-6)에 쓰인다.
 *
 * 근거: 설계 명세서 5.2 · 기획서 7장
 */

export const occasionType = z.enum(['FUNERAL', 'CELEBRATION', 'GIFT'])
export type OccasionType = z.infer<typeof occasionType>

export const saleStatus = z.enum(['ON_SALE', 'PAUSED', 'ENDED'])
export type SaleStatus = z.infer<typeof saleStatus>

export const handlingStatus = z.enum(['HANDLING', 'PAUSED', 'STOPPED'])
export type HandlingStatus = z.infer<typeof handlingStatus>

// ── 1. 포비즈 상품 ──────────────────────────────────────────

export const upsertProductRequest = z.object({
  name: z.string().min(1).max(100),
  // 분류는 운영자용 세부 라벨(고객은 occasionType으로 봄) — 선택(빈 값 허용, BIZ-122). NOT NULL엔 ''로 저장.
  category: z.string().max(50).optional(),
  occasionType,
  imageUrl: z.string().url().max(500).optional(),
  /** 운영자 업로드 이미지 키(비공개 S3). 있으면 imageUrl보다 우선(BIZ-122). */
  imageKey: z.string().max(300).nullable().optional(),
  summary: z.string().max(500).optional(),
  spec: z.string().max(500).optional(),
  // 상품 상세 신뢰정보(BIZ-122). 비우면 web이 유형별 정적 문구로 폴백한다.
  inclusion: z.string().max(1000).nullable().optional(),
  deliveryNote: z.string().max(1000).nullable().optional(),
  caution: z.string().max(1000).nullable().optional(),
  // 상한 = int4 최대. 넘기면 DB 22003(500) 대신 계약 단계에서 400으로 막는다.
  basePrice: z.number().int().min(0).max(2_147_483_647),
  saleStatus: saleStatus.optional(), // 생략 시 ON_SALE
  displayOrder: z.number().int().optional(),
})
export type UpsertProductRequest = z.infer<typeof upsertProductRequest>

/** 여러 상품 일괄 처리 (운영자). */
const productIds = z.array(z.number().int().positive()).min(1).max(500)
export const bulkDeleteProductsRequest = z.object({ productIds })
export type BulkDeleteProductsRequest = z.infer<typeof bulkDeleteProductsRequest>
export const bulkSaleStatusRequest = z.object({ productIds, saleStatus })
export type BulkSaleStatusRequest = z.infer<typeof bulkSaleStatusRequest>

/** 노출 순서 재정렬 (운영자). productIds 순서대로 display_order를 0..n-1로 재부여한다.
 *  카탈로그는 유형별로 나뉘어 노출되므로, 보통 한 유형(occasion)의 상품 목록을 통째로 보낸다. */
export const reorderProductsRequest = z.object({ productIds })
export type ReorderProductsRequest = z.infer<typeof reorderProductsRequest>

export const productResponse = z.object({
  id: z.number().int(),
  name: z.string(),
  category: z.string(),
  occasionType,
  imageUrl: z.string().nullable(),
  /** 업로드 이미지 키(있으면 서버가 imageUrl에 서명 URL을 채워 내려준다). 편집 시 보존용. */
  imageKey: z.string().nullable(),
  summary: z.string().nullable(),
  spec: z.string().nullable(),
  inclusion: z.string().nullable(),
  deliveryNote: z.string().nullable(),
  caution: z.string().nullable(),
  basePrice: z.number().int(),
  saleStatus,
  displayOrder: z.number().int(),
})
export type ProductResponse = z.infer<typeof productResponse>

/**
 * 목록 항목 = 상품 + 연결 개수. 운영자 목록에서 "이 상품에 몇 개 업체가 공급하고
 * 몇 개 기업이 파는지"를 한눈에 보여주려고 개수를 함께 내려준다(BIZ 상품 목록 개선).
 */
export const productListItemResponse = productResponse.extend({
  vendorCount: z.number().int(), // 공급 업체 수 (vendor_product)
  companyCount: z.number().int(), // 판매 기업 수 (company_product)
})
export type ProductListItemResponse = z.infer<typeof productListItemResponse>

export const productListResponse = z.object({ products: z.array(productListItemResponse) })
export type ProductListResponse = z.infer<typeof productListResponse>

// ── 2. 업체 공급 상품 ───────────────────────────────────────

export const upsertVendorProductRequest = z.object({
  vendorId: z.number().int(),
  productId: z.number().int(),
  vendorProductName: z.string().min(1).max(100),
  vendorProductCode: z.string().max(50).optional(),
  baseSupplyPrice: z.number().int().min(0).max(2_147_483_647),
  handlingStatus: handlingStatus.optional(),
})
export type UpsertVendorProductRequest = z.infer<typeof upsertVendorProductRequest>

export const vendorProductResponse = z.object({
  id: z.number().int(),
  vendorId: z.number().int(),
  productId: z.number().int(),
  productName: z.string(), // 조인해서 채운다 (편의)
  vendorProductName: z.string(),
  vendorProductCode: z.string().nullable(),
  baseSupplyPrice: z.number().int(),
  handlingStatus,
})
export type VendorProductResponse = z.infer<typeof vendorProductResponse>

export const vendorProductListResponse = z.object({
  vendorProducts: z.array(vendorProductResponse),
})
export type VendorProductListResponse = z.infer<typeof vendorProductListResponse>

/** 공급가 수정 (vendor_product 단건). vendorId·productId는 행에 고정되어 바꾸지 않는다. */
export const updateVendorProductRequest = z.object({
  vendorProductName: z.string().min(1).max(100),
  vendorProductCode: z.string().max(50).nullable().optional(),
  baseSupplyPrice: z.number().int().min(0).max(2_147_483_647),
  handlingStatus: handlingStatus.optional(),
})
export type UpdateVendorProductRequest = z.infer<typeof updateVendorProductRequest>

/**
 * 상품 상세 허브(BIZ-31)가 쓰는 productId 역방향 조회 응답.
 * 한 상품을 공급하는 업체 목록 — 어느 업체인지 보이게 vendorName을 함께 준다.
 */
export const productVendorResponse = vendorProductResponse.extend({
  vendorName: z.string(),
})
export type ProductVendorResponse = z.infer<typeof productVendorResponse>

export const productVendorListResponse = z.object({
  vendorProducts: z.array(productVendorResponse),
})
export type ProductVendorListResponse = z.infer<typeof productVendorListResponse>

// ── 3. 기업별 상품 설정 ─────────────────────────────────────

/**
 * 기업별 상품 설정 upsert. **부분 갱신(PATCH)** 의미다 — 보낸 필드만 바뀌고,
 * 안 보낸 필드는 기존 값이 보존된다. 새로 만드는 경우 안 보낸 필드는 기본값.
 * (운영자가 가격만 바꾸려다 다른 설정을 날리는 사고를 막기 위한 결정)
 */
export const upsertCompanyProductRequest = z
  .object({
    productId: z.number().int(),
    salePrice: z.number().int().min(0).max(2_147_483_647).nullable().optional(), // null이면 product.base_price 사용
    isDefault: z.boolean().optional(),
    isFixed: z.boolean().optional(),
    allowChange: z.boolean().optional(),
    skipSelection: z.boolean().optional(),
    customName: z.string().max(100).nullable().optional(),
    displayOrder: z.number().int().optional(),
    saleFrom: z.string().date().nullable().optional(),
    saleTo: z.string().date().nullable().optional(),
  })
  // 판매 시작일이 종료일보다 앞설 수 없다. 계약에서 걸러 DB CHECK(500)를 막는다.
  .refine((d) => !d.saleFrom || !d.saleTo || d.saleFrom <= d.saleTo, {
    message: '판매 종료일은 시작일 이후여야 합니다',
    path: ['saleTo'],
  })
export type UpsertCompanyProductRequest = z.infer<typeof upsertCompanyProductRequest>

/** 운영자가 보는 기업별 상품 설정. 원본 상품 정보와 설정을 함께. */
export const companyProductResponse = z.object({
  id: z.number().int(),
  productId: z.number().int(),
  productName: z.string(),
  category: z.string(),
  occasionType,
  basePrice: z.number().int(),
  salePrice: z.number().int().nullable(),
  /** 적용 판매가: salePrice ?? basePrice (설계 7.3) */
  effectivePrice: z.number().int(),
  isDefault: z.boolean(),
  isFixed: z.boolean(),
  allowChange: z.boolean(),
  skipSelection: z.boolean(),
  customName: z.string().nullable(),
  displayOrder: z.number().int(),
  saleFrom: z.string().nullable(),
  saleTo: z.string().nullable(),
})
export type CompanyProductResponse = z.infer<typeof companyProductResponse>

export const companyProductListResponse = z.object({
  companyProducts: z.array(companyProductResponse),
})
export type CompanyProductListResponse = z.infer<typeof companyProductListResponse>

/**
 * 상품 상세 허브(BIZ-31)가 쓰는 productId 역방향 조회 응답.
 * 한 상품을 파는 기업 목록 — companyProductResponse엔 companyId·companyName이 없어
 * (기업별 조회는 companyId를 이미 알기에) 여기서 함께 채운다.
 */
export const productCompanyResponse = companyProductResponse.extend({
  companyId: z.number().int(),
  companyName: z.string(),
})
export type ProductCompanyResponse = z.infer<typeof productCompanyResponse>

export const productCompanyListResponse = z.object({
  companyProducts: z.array(productCompanyResponse),
})
export type ProductCompanyListResponse = z.infer<typeof productCompanyListResponse>

/**
 * 전체 기업에 이 상품을 한 번에 판매 등록 (BIZ-31). 이미 연결된 기업은 건너뛴다.
 * salePrice 생략/null이면 기준가(product.base_price)를 적용한다.
 */
export const bulkConnectCompaniesRequest = z.object({
  salePrice: z.number().int().min(0).max(2_147_483_647).nullable().optional(),
})
export type BulkConnectCompaniesRequest = z.infer<typeof bulkConnectCompaniesRequest>

/**
 * 빠른 등록 (BIZ-124) — 상품 생성 + 공급 연결 + (선택) 전 기업 판매 등록을 한 번에.
 * saleStatus는 받지 않는다: 공급이 있으면 ON_SALE, 없으면 PAUSED로 서버가 정한다(BIZ-31).
 */
export const quickCreateProductRequest = upsertProductRequest.omit({ saleStatus: true }).extend({
  supply: z
    .object({
      vendorId: z.number().int().positive(),
      baseSupplyPrice: z.number().int().min(0).max(2_147_483_647),
      /** 생략 시 상품명을 그대로 쓴다. */
      vendorProductName: z.string().min(1).max(100).optional(),
    })
    .optional(),
  /** 전체 기업의 상품 목록(company_product)에도 기준가로 연결한다(BIZ-31 bulkConnect 의미). */
  sellToAllCompanies: z.boolean().optional(),
})
export type QuickCreateProductRequest = z.infer<typeof quickCreateProductRequest>

export const quickCreateProductResponse = z.object({
  product: productResponse,
  vendorProduct: vendorProductResponse.nullable(),
  connectedCompanies: z.number().int(),
})
export type QuickCreateProductResponse = z.infer<typeof quickCreateProductResponse>

// ── 4. 기업 카탈로그 (주문용) ───────────────────────────────

/**
 * 기업 담당자가 주문할 때 보는 상품 카드(기획서 5.4).
 * 판매가는 적용가로 이미 계산돼 있고, 공급가·내부 정보는 없다.
 * 판매 기간(saleFrom/To)과 판매 상태로 필터된 것만 나온다.
 */
export const catalogItem = z.object({
  productId: z.number().int(),
  /** custom_name ?? product.name */
  name: z.string(),
  category: z.string(),
  occasionType,
  imageUrl: z.string().nullable(),
  summary: z.string().nullable(),
  /** 상품 상세 신뢰정보(BIZ-122). null이면 web이 유형별 정적 문구로 폴백한다. */
  inclusion: z.string().nullable(),
  deliveryNote: z.string().nullable(),
  caution: z.string().nullable(),
  price: z.number().int(),
  isDefault: z.boolean(),
  isFixed: z.boolean(),
  skipSelection: z.boolean(),
})
export type CatalogItem = z.infer<typeof catalogItem>

export const catalogResponse = z.object({ items: z.array(catalogItem) })
export type CatalogResponse = z.infer<typeof catalogResponse>

export const ProductErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  PRODUCT_NOT_FOUND: 'PRODUCT_NOT_FOUND',
  VENDOR_PRODUCT_NOT_FOUND: 'VENDOR_PRODUCT_NOT_FOUND', // 공급가 수정 대상(vendor_product) 없음
  VENDOR_NOT_FOUND: 'VENDOR_NOT_FOUND',
  COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
  DUPLICATE: 'DUPLICATE', // 같은 기업-상품, 업체-상품 조합 중복
  DEFAULT_CONFLICT: 'DEFAULT_CONFLICT', // 기본상품 동시 지정 경합
  PRODUCT_IN_USE: 'PRODUCT_IN_USE', // 주문 이력이 있어 삭제 불가(판매 중지로)
  NO_SUPPLY: 'NO_SUPPLY', // 취급 중 공급 업체가 없어 판매 중(ON_SALE)으로 둘 수 없음(BIZ-31)
  FORBIDDEN: 'FORBIDDEN',
} as const
export type ProductErrorCode = (typeof ProductErrorCode)[keyof typeof ProductErrorCode]
